import { createViewerAction } from "../../../core/make-action";
import ViewerMethod from "../../../core/ViewerMethod";
import {
  getDisplayMode,
  getFontFamily,
  getFontSize,
  getLineHeight,
  getPageMargin,
  getReaderView,
  isEnhancedFormattingEnabled,
} from "./selectors";
import { getBookInfo } from "../../bookinfo/selectors";
import { CC_DEFAULT_CONFIG_0, getDefaultSettingConfig } from "./utils";

const addEnhancedFormattingCSS = createViewerAction(
  ViewerMethod.ADD_ENHANCED_FORMATTING_CSS
);
const removeEnhancedFormattingCSS = createViewerAction(
  ViewerMethod.REMOVE_ENHANCED_FORMATTING_CSS
);
const baseSetReaderView = createViewerAction(ViewerMethod.SET_READER_VIEW);
const baseSetFontFamily = createViewerAction(ViewerMethod.SET_FONT_FAMILY);
const baseSetTextSize = createViewerAction(ViewerMethod.SET_TEXT_SIZE);
const baseSetLineHeight = createViewerAction(ViewerMethod.SET_LINE_HEIGHT);
const baseSetPageMargin = createViewerAction(ViewerMethod.SET_PAGE_MARGIN);
const baseSetDisplayMode = createViewerAction(ViewerMethod.SET_DISPLAY_MODE);

function createSetter(baseAction) {
  return function (...args) {
    return function (dispatch, getState) {
      if (!isEnhancedFormattingEnabled(getState())) {
        dispatch(addEnhancedFormattingCSS());
      }
      dispatch(baseAction(...args));
    };
  };
}

export const setReaderView = createSetter(baseSetReaderView);
export const setFontFamily = createSetter(baseSetFontFamily);
export const setTextSize = createSetter(baseSetTextSize);
export const setLineHeight = createSetter(baseSetLineHeight);
export const setPageMargin = createSetter(baseSetPageMargin);
export const setDisplayMode = createSetter(baseSetDisplayMode);

export function resetFormatting() {
  return (dispatch) => dispatch(removeEnhancedFormattingCSS());
}

const availableSettings = {
  readerView: [getReaderView, setReaderView],
  fontFamily: [getFontFamily, setFontFamily],
  fontSize: [getFontSize, setTextSize],
  lineHeight: [getLineHeight, setLineHeight],
  pageMargin: [getPageMargin, setPageMargin],
  displayMode: [getDisplayMode, setDisplayMode],
};

export type EnhancedContentControls = Partial<
  {
    [K in keyof typeof availableSettings]: string | number;
  }
>;

export function applyContentControlSettings(
  reset = false,
  overrides: EnhancedContentControls = {}
) {
  return function (dispatch, getState) {
    if (!getBookInfo(getState()).isFixedLayout) {
      const hasDefaultSettingsConfig = getBookInfo(getState()).features.find(
        (feature) => feature.name.toLowerCase() === CC_DEFAULT_CONFIG_0
      );

      if (!hasDefaultSettingsConfig && reset) {
        return dispatch(resetFormatting());
      }

      const settings =
        hasDefaultSettingsConfig &&
        (!isEnhancedFormattingEnabled(getState()) || reset) &&
        getDefaultSettingConfig();

      Object.entries(availableSettings).forEach(([key, [getter, setter]]) => {
        let settingValue;
        const current = getter(getState());
        // note current might be undefined if a new setting is added
        // and only a partial set has been stored previously on server
        if (current?.enabled && !reset) {
          settingValue = current.value;
        } else if (settings && settings[key]) {
          settingValue = settings[key];
        }
        if (overrides[key]) {
          settingValue = overrides[key];
        }

        if (settingValue) {
          return dispatch(setter(settingValue));
        }
      });
    }
  };
}
